import React, { useState } from 'react';
import { Button } from './ui/button';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t, language, toggleLanguage, isRTL } = useLanguage();

  const navLinks = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.products'), href: '#products' },
    { name: t('nav.whyUs'), href: '#why-us' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="#home" className="flex items-center">
              <img 
                src="/images/logo.png" 
                alt="AFAQ ALNABTH Medical Supplies" 
                className="h-10 sm:h-14 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation - Only show on xl screens (1280px+) */}
          <nav className="hidden xl:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-blue rounded-lg hover:bg-blue-50 transition-all duration-200"
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* Right Side Actions - Desktop */}
          <div className="hidden xl:flex items-center gap-3">
            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-brand-blue rounded-lg hover:bg-blue-50 transition-all duration-200 border border-slate-200"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'en' ? 'العربية' : 'English'}</span>
            </button>
            
            <a href="tel:+971000000000" className="flex items-center gap-2 text-sm text-slate-600 hover:text-brand-blue transition-colors">
              <Phone className="w-4 h-4" />
              <span dir="ltr">+971 XX XXX XXXX</span>
            </a>
            <Button 
              onClick={() => scrollToSection('#contact')}
              className="bg-brand-red hover:bg-red-700 text-white px-6 shadow-lg shadow-red-600/20 hover:shadow-red-600/30 transition-all duration-300"
            >
              {t('nav.getInTouch')}
            </Button>
          </div>

          {/* Mobile/Tablet Menu Button */}
          <div className="xl:hidden flex items-center gap-2">
            <button
              onClick={toggleLanguage}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors border border-slate-200"
              aria-label="Switch Language"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Toggle Menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile/Tablet Navigation */}
        {isMenuOpen && (
          <div className="xl:hidden py-4 border-t border-slate-100 bg-white">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className={`px-4 py-3 text-sm font-medium text-slate-600 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all duration-200 ${isRTL ? 'text-right' : 'text-left'}`}
                >
                  {link.name}
                </button>
              ))}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <a 
                  href="tel:+971000000000" 
                  className={`flex items-center gap-2 px-4 py-2 text-sm text-slate-600 ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <Phone className="w-4 h-4" />
                  <span dir="ltr">+971 XX XXX XXXX</span>
                </a>
              </div>
              <Button 
                onClick={() => scrollToSection('#contact')}
                className="mt-4 mx-4 bg-brand-red hover:bg-red-700 text-white"
              >
                {t('nav.getInTouch')}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
